// path: frontend/src/pages/PrivacyPolicy.tsx
import { useEffect } from "react";
import { Shield, Eye, Lock, Database, Cookie, Mail, Globe, UserCheck } from "lucide-react";

export default function PrivacyPolicy() {
    useEffect(() => {
        document.title = "Privacy Policy – AutoQuiz AI";
    }, []);

    return (
        <div className="max-w-4xl mx-auto p-6 text-slate-900 dark:text-slate-100">
            {/* Header */}
            <div className="mb-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 mb-4">
                    <Shield size={24} />
                </div>
                <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
                <p className="text-slate-600 dark:text-slate-400">
                    Last updated: September 12, 2026
                </p>
            </div>

            {/* Introduction */}
            <div className="card p-6 mb-6 bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-900/30">
                <p className="text-sm leading-relaxed text-blue-900 dark:text-blue-200">
                    AutoQuiz AI is operated by MC SANTE PLUS SERVICES LLC. This Privacy
                    Policy explains how we collect, use, disclose, and safeguard your
                    information when you use our web application. Please read this privacy policy carefully. If you do
                    not agree with the terms of this privacy policy, please do not access the
                    application.
                </p>
            </div>

            {/* Main Content */}
            <div className="space-y-6">
                {/* Information We Collect */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Database className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        Information We Collect
                    </h2>

                    <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
                        <div>
                            <h3 className="font-semibold text-base mb-2">Personal Information</h3>
                            <p className="mb-2">We may collect personal information that you voluntarily provide to us when you:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Register for an account</li>
                                <li>Use our services to generate quiz videos</li>
                                <li>Purchase credit packs</li>
                                <li>Contact us for support</li>
                            </ul>
                            <p className="mt-2">This information may include:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Email address</li>
                                <li>Name</li>
                                <li>Payment information (processed securely through third-party providers)</li>
                                <li>Account preferences and settings</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold text-base mb-2">Usage Data</h3>
                            <p className="mb-2">We automatically collect certain information when you use our application:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Device information (browser type, operating system)</li>
                                <li>IP address and general location</li>
                                <li>Pages visited and features used</li>
                                <li>Time and date of access</li>
                                <li>Quiz generation history and preferences</li>
                                <li>Video viewing analytics</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold text-base mb-2">Content You Create</h3>
                            <p>We store the quiz videos you generate, including:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li>Quiz questions and answers</li>
                                <li>Video content and metadata</li>
                                <li>Background selections and customizations</li>
                                <li>Generated video files</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* How We Use Your Information */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />
                        How We Use Your Information
                    </h2>

                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>We use the information we collect to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Provide, operate, and maintain our services</li>
                            <li>Process credit-pack purchases and maintain credit balances</li>
                            <li>Create and manage your account</li>
                            <li>Generate and store your quiz videos</li>
                            <li>Improve and personalize your experience</li>
                            <li>Send you technical notices and support messages</li>
                            <li>Respond to your comments and questions</li>
                            <li>Analyze usage patterns to improve our application</li>
                            <li>Detect and prevent fraud or abuse</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </div>
                </section>

                {/* Authentication & Third-Party Services */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        Authentication & Third-Party Services
                    </h2>

                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <div>
                            <h3 className="font-semibold mb-2">Firebase Authentication</h3>
                            <p>
                                We use Google Firebase for user authentication. When you sign in with
                                Google or create an account, Firebase collects and processes your
                                authentication data according to Google's Privacy Policy.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Payment Processing</h3>
                            <p>
                                Payment transactions are processed through secure third-party payment
                                processors through Stripe. We do not store your complete credit card
                                information on our servers. Payment data is handled according to
                                PCI-DSS standards.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-2">Analytics</h3>
                            <p>
                                We may use analytics services (such as Plausible Analytics) to understand
                                how users interact with our application. These services may collect
                                information about your usage patterns in an aggregated, anonymized form.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Data Storage and Security */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Lock className="w-5 h-5 text-red-600 dark:text-red-400" />
                        Data Storage and Security
                    </h2>

                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <p>
                            We implement appropriate technical and organizational security measures
                            to protect your personal information, including:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Encryption of data in transit using HTTPS/TLS</li>
                            <li>Secure authentication via Firebase</li>
                            <li>Regular security assessments</li>
                            <li>Access controls and authentication requirements</li>
                            <li>Secure cloud storage (Firebase/Google Cloud)</li>
                        </ul>
                        <p className="mt-3">
                            <strong>However</strong>, no method of transmission over the internet is
                            100% secure. While we strive to protect your information, we cannot
                            guarantee absolute security.
                        </p>
                    </div>
                </section>

                {/* Cookies and Tracking */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Cookie className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        Cookies and Tracking Technologies
                    </h2>

                    <div className="text-sm space-y-3 text-slate-700 dark:text-slate-300">
                        <p>We use cookies and similar tracking technologies to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Maintain your login session</li>
                            <li>Remember your preferences (theme, settings)</li>
                            <li>Analyze usage patterns</li>
                            <li>Improve site performance</li>
                        </ul>
                        <p className="mt-3">
                            You can control cookies through your browser settings. However, disabling
                            cookies may limit your ability to use certain features of our application.
                        </p>
                    </div>
                </section>

                {/* Data Retention */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Database className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                        Data Retention
                    </h2>

                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>We retain your personal information for as long as:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>Your account is active</li>
                            <li>Needed to provide you services</li>
                            <li>Required to comply with legal obligations</li>
                            <li>Necessary to resolve disputes or enforce agreements</li>
                        </ul>
                        <p className="mt-3">
                            When you delete your account, we will delete or anonymize your personal
                            information within a reasonable timeframe, unless we are required to
                            retain it for legal purposes.
                        </p>
                    </div>
                </section>

                {/* Your Rights */}
                <section className="card p-6 bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/30">
                    <h2 className="text-xl font-semibold mb-4 text-green-900 dark:text-green-200">
                        Your Privacy Rights
                    </h2>

                    <div className="text-sm space-y-2 text-green-800 dark:text-green-300">
                        <p className="font-semibold mb-2">You have the right to:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li><strong>Access</strong> your personal data</li>
                            <li><strong>Correct</strong> inaccurate or incomplete data</li>
                            <li><strong>Delete</strong> your account and personal data</li>
                            <li><strong>Export</strong> your data in a portable format</li>
                            <li><strong>Opt-out</strong> of marketing communications</li>
                            <li><strong>Restrict</strong> processing of your data</li>
                            <li><strong>Object</strong> to processing based on legitimate interests</li>
                        </ul>
                        <p className="mt-3">
                            To exercise these rights, please contact us at{" "}
                            <a
                                href="mailto:privacy@findtheideas.com"
                                className="underline hover:no-underline"
                            >
                                privacy@findtheideas.com
                            </a>
                        </p>
                    </div>
                </section>

                {/* Children's Privacy */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Children's Privacy</h2>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                        Our service is not intended for children under 13 years of age. We do not
                        knowingly collect personal information from children under 13. If you are
                        a parent or guardian and believe your child has provided us with personal
                        information, please contact us, and we will delete such information.
                    </p>
                </section>

                {/* International Users */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        International Users
                    </h2>
                    <div className="text-sm space-y-2 text-slate-700 dark:text-slate-300">
                        <p>
                            Our services are hosted in the United States. If you access our
                            application from outside the United States, your information may be
                            transferred to, stored, and processed in the United States or other
                            countries.
                        </p>
                        <p>
                            By using our services, you consent to the transfer of your information
                            to countries that may have different data protection laws than your
                            country of residence.
                        </p>
                    </div>
                </section>

                {/* Changes to Privacy Policy */}
                <section className="card p-6">
                    <h2 className="text-xl font-semibold mb-4">Changes to This Privacy Policy</h2>
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                        We may update this Privacy Policy from time to time. We will notify you
                        of any changes by posting the new Privacy Policy on this page and updating
                        the "Last updated" date. You are advised to review this Privacy Policy
                        periodically for any changes. Changes are effective when posted on this page.
                    </p>
                </section>

                {/* Contact */}
                <section className="card p-6 bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-900/30">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        Contact Us
                    </h2>
                    <div className="text-sm text-blue-900 dark:text-blue-200 space-y-2">
                        <p>
                            If you have any questions about this Privacy Policy or our data
                            practices, please contact us:
                        </p>
                        <ul className="space-y-1">
                            <li>
                                <strong>Email:</strong>{" "}
                                <a
                                    href="mailto:privacy@findtheideas.com"
                                    className="underline hover:no-underline"
                                >
                                    privacy@findtheideas.com
                                </a>
                            </li>
                            <li>
                                <strong>Support:</strong>{" "}
                                <a
                                    href="mailto:services@findtheideas.com"
                                    className="underline hover:no-underline"
                                >
                                    services@findtheideas.com
                                </a>
                            </li>
                        </ul>
                    </div>
                </section>
            </div>
        </div>
    );
}